/**
 * Planet and Moon rendering with procedural surface shaders, cloud layer,
 * atmosphere glow, particle wisps, and visual gravity ring indicators.
 * Planet is at center, moon angle driven by game loop (store).
 * Moon uses angular velocity extrapolation (MOON_ORBIT_SPEED is constant),
 * producing exact sub-tick positioning.
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import type { WorldSetup } from '@warmancer/shared';
import { TICK_DELTA, MOON_ORBIT_SPEED, GRAVITY_RANGE_MULTIPLIER } from '@warmancer/shared';
import { useGameStore } from '../stores/gameStore';
import {
  planetVertexShader,
  planetFragmentShader,
  cloudVertexShader,
  cloudFragmentShader,
  atmosphereVertexShader,
  atmosphereFragmentShader,
  moonVertexShader,
  moonFragmentShader,
} from './shaders/planetShaders.js';
import { createAtmosphereWispsFromConfig } from './effects/planetParticles.js';
import { particleManager } from './effects/ParticleManager.js';
import type { ParticleSystem } from 'three.quarks';
import { useParticleEffectStore } from '../stores/particleEffectStore.js';
import type { AtmosphereWispsConfig } from '@warmancer/shared';
import { DEFAULT_ATMOSPHERE_WISPS } from '@warmancer/shared';

interface PlanetProps {
  readonly setup: WorldSetup;
}

const PLANET_GLOW_COLOR = '#3355aa';
const PLANET_GLOW_INTENSITY = 5000;
const PLANET_GLOW_DISTANCE = 200;
const PLANET_GLOW_DECAY = 2;
// HDR colors survive Uncharted 2 tone mapping (crushes LDR values below ~0.1 to near-zero).
// MeshBasicMaterial writes directly to HalfFloat framebuffer without clamping.
const GRAVITY_RING_COLOR = new THREE.Color(0.3, 0.6, 2.0);
const ORBIT_PATH_COLOR = new THREE.Color(0.6, 0.6, 1.2);
const ORBIT_SEGMENTS = 128;

function generateCirclePoints(radius: number, segments: number, y: number): Array<[number, number, number]> {
  const points: Array<[number, number, number]> = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    points.push([Math.cos(angle) * radius, y, Math.sin(angle) * radius]);
  }
  return points;
}

export function Planet({ setup }: PlanetProps) {
  const moonGroupRef = useRef<THREE.Group>(null);
  const planetGroupRef = useRef<THREE.Group>(null);
  const wispSystemRef = useRef<ParticleSystem | null>(null);

  const orbitRadius = setup.moon.orbitRadius;
  const planetX = setup.planet.x;
  const planetY = setup.planet.y;
  const orbitPoints = useMemo(
    () => generateCirclePoints(orbitRadius, ORBIT_SEGMENTS, -0.3).map(
      ([x, y, z]) => [x + planetX, y, z + planetY] as [number, number, number]
    ),
    [orbitRadius, planetX, planetY],
  );

  // Planet surface shader material
  const planetMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: planetVertexShader,
    fragmentShader: planetFragmentShader,
    uniforms: {
      uTime: { value: 0 },
      uRadius: { value: setup.planet.radius },
    },
  }), [setup.planet.radius]);

  // Cloud layer shader material
  const cloudMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: cloudVertexShader,
    fragmentShader: cloudFragmentShader,
    uniforms: {
      uTime: { value: 0 },
    },
    transparent: true,
    depthWrite: false,
  }), []);

  // Atmosphere glow shader material (BackSide)
  const atmosphereMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: atmosphereVertexShader,
    fragmentShader: atmosphereFragmentShader,
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
  }), []);

  // Moon surface shader material
  const moonMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: moonVertexShader,
    fragmentShader: moonFragmentShader,
  }), []);

  // Atmosphere wisps config from particle effect store
  const wispConfig = useParticleEffectStore(
    (s) => s.effects.atmosphere_wisps as AtmosphereWispsConfig | undefined,
  ) ?? DEFAULT_ATMOSPHERE_WISPS;

  // Atmosphere particle wisps — rebuilds when config changes
  useEffect(() => {
    if (!particleManager.isInitialized()) return;

    const wisps = createAtmosphereWispsFromConfig(setup.planet.radius, wispConfig);
    wisps.emitter.position.set(setup.planet.x, 0, setup.planet.y);

    if (planetGroupRef.current) {
      planetGroupRef.current.add(wisps.emitter);
    }

    const renderer = particleManager.getRenderer();
    renderer.addSystem(wisps);
    wispSystemRef.current = wisps;

    return () => {
      renderer.deleteSystem(wisps);
      wisps.dispose();
      if (planetGroupRef.current) {
        planetGroupRef.current.remove(wisps.emitter);
      }
      wispSystemRef.current = null;
    };
  }, [setup.planet.radius, setup.planet.x, setup.planet.y, wispConfig]);

  // Moon has constant angular velocity, so sub-tick extrapolation is exact:
  // visualAngle = physicsAngle + MOON_ORBIT_SPEED * TICK_DELTA * alpha
  useFrame((_, delta) => {
    if (!moonGroupRef.current) return;
    const targetAngle = useGameStore.getState().moonAngle;
    const alpha = useGameStore.getState().renderAlpha;

    const angle = targetAngle + MOON_ORBIT_SPEED * TICK_DELTA * alpha;
    moonGroupRef.current.position.x = Math.cos(angle) * setup.moon.orbitRadius;
    moonGroupRef.current.position.z = Math.sin(angle) * setup.moon.orbitRadius;

    // Animate shader uniforms
    const time = planetMaterial.uniforms.uTime.value + delta;
    planetMaterial.uniforms.uTime.value = time;
    cloudMaterial.uniforms.uTime.value = time;
  });

  return (
    <group ref={planetGroupRef}>
      {/* Planet glow light */}
      <pointLight
        position={[setup.planet.x, 2, setup.planet.y]}
        color={PLANET_GLOW_COLOR}
        intensity={PLANET_GLOW_INTENSITY}
        distance={PLANET_GLOW_DISTANCE}
        decay={PLANET_GLOW_DECAY}
      />

      {/* Planet body — procedural surface shader */}
      <mesh position={[setup.planet.x, 0, setup.planet.y]} material={planetMaterial}>
        <sphereGeometry args={[setup.planet.radius, 64, 32]} />
      </mesh>

      {/* Cloud layer — slightly larger translucent sphere */}
      <mesh position={[setup.planet.x, 0, setup.planet.y]} material={cloudMaterial}>
        <sphereGeometry args={[setup.planet.radius + 0.5, 64, 32]} />
      </mesh>

      {/* Atmosphere glow shell — BackSide rendered */}
      <mesh position={[setup.planet.x, 0, setup.planet.y]} material={atmosphereMaterial}>
        <sphereGeometry args={[setup.planet.radius * 1.25, 48, 24]} />
      </mesh>

      {/* Planet gravity range boundary ring */}
      <mesh
        position={[setup.planet.x, -0.5, setup.planet.y]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <ringGeometry args={[setup.planet.radius * GRAVITY_RANGE_MULTIPLIER - 1, setup.planet.radius * GRAVITY_RANGE_MULTIPLIER, 64]} />
        <meshBasicMaterial
          color={GRAVITY_RING_COLOR}
          transparent
          opacity={0.6}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Moon orbit path (dotted line) */}
      <Line
        points={orbitPoints}
        dashed
        dashSize={6}
        gapSize={6}
        color={ORBIT_PATH_COLOR}
        lineWidth={1.5}
        opacity={0.6}
        transparent
      />

      {/* Moon */}
      <group ref={moonGroupRef}>
        <mesh position={[0, 0, 0]} material={moonMaterial}>
          <sphereGeometry args={[setup.moon.radius, 32, 16]} />
        </mesh>

        {/* Moon gravity range boundary ring */}
        <mesh position={[0, -0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[setup.moon.radius * GRAVITY_RANGE_MULTIPLIER - 0.5, setup.moon.radius * GRAVITY_RANGE_MULTIPLIER, 48]} />
          <meshBasicMaterial
            color={GRAVITY_RING_COLOR}
            transparent
            opacity={0.5}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>
    </group>
  );
}
